import React, { useCallback } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation } from '@fortawesome/pro-light-svg-icons/faTriangleExclamation';
import { faChevronRight } from '@fortawesome/pro-light-svg-icons/faChevronRight';
import { TypedMemo } from '../../common/types';
import { Cover } from '../../music24/components/primitives/Cover';
import { CatalogItem } from '../../../redux/api/fymAPI.types';
import { catalogArtworkSrc } from '../catalog/catalogArtwork';
import { CreationItemCard } from './CreationItemCard';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the sheet; each part uses its own slice of it */
import styles from './SMLCreationSheet.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface CreationDuplicateStageProps {
  /** The catalog row that already carries a page. */
  item: CatalogItem;
  slugPrefix: string;
  onOpenExisting: () => void;
}

/**
 * A release the artist already has a page for.
 *
 * Nothing stopped a second one before, and nobody noticed until the numbers
 * were split across two pages and the wrong one was the one being posted.
 */
const CreationDuplicateStageComponent: React.FC<CreationDuplicateStageProps> = ({
  item,
  slugPrefix,
  onOpenExisting,
}) => {
  const intl = useIntl();
  const open = useCallback(() => onOpenExisting(), [onOpenExisting]);
  const isPublished = item.smartLinkStatus === 'published';

  return (
    <>
      <CreationItemCard
        title={item.title}
        subtitle={item.artistName}
        imageUrl={catalogArtworkSrc(item, 96)}
        source={item.releaseType ?? undefined}
      />

      <div className={styles.warn}>
        <FontAwesomeIcon icon={faTriangleExclamation} className={styles.warnIcon} />
        <p className={styles.warnText}>
          <span className={styles.warnStrong}>
            {intl.formatMessage({
              id: 'app.sml.create.duplicate.title',
              defaultMessage: 'You already have a page for this.',
            })}
          </span>{' '}
          {intl.formatMessage({
            id: 'app.sml.create.duplicate.body',
            defaultMessage:
              'Two live pages for one release split your numbers and confuse whoever posts the wrong one.',
          })}
        </p>
      </div>

      <div className={styles.rows}>
        <button type="button" className={clsx(styles.row, styles.rowSelected)} onClick={open}>
          <Cover src={catalogArtworkSrc(item, 42)} label={item.title} size={42} />
          <span className={styles.rowInfo}>
            <span className={styles.rowName}>{item.title}</span>
            <span className={styles.rowSub}>{`${slugPrefix}${item.smartLinkSlug ?? ''}`}</span>
          </span>
          <span className={clsx(styles.pill, isPublished ? styles.pillLive : styles.pillOut)}>
            {isPublished
              ? intl.formatMessage({ id: 'app.sml.create.duplicate.live', defaultMessage: 'Live' })
              : intl.formatMessage({ id: 'app.sml.create.duplicate.draft', defaultMessage: 'Draft' })}
          </span>
          <FontAwesomeIcon icon={faChevronRight} className={styles.rowGo} />
        </button>
      </div>
    </>
  );
};

export const CreationDuplicateStage = TypedMemo(CreationDuplicateStageComponent);
